import {
  OmniElement,
  html,
  css,
  repeat,
  styleMap,
  nothing,
  classMap,
} from 'omni-ui';
import './campaign-audience-add-segments.js';
import './campaign-audience-segment-info.js';
import './campaign-audience-rfc-sliders.js';
import { rfcColors } from './campaign-audience-rfc-legend.js';

const formatAudienceNum = num => {
  const suffixes = ['', 'K', 'M', 'B', 'T'];
  const tier = Math.floor(Math.log10(Math.abs(num)) / 3);
  if (tier === 0) {
    return num.toLocaleString();
  }
  const suffix = suffixes[tier];
  const scaled = num / 10 ** (tier * 3);
  const formattedNum = scaled.toFixed(1);
  return `${formattedNum} ${suffix}`;
};

export class CampaignAudienceFunnelViz extends OmniElement {
  static get styles() {
    return [
      super.styles,
      css`
        :host {
          height: 100%;
        }
        omni-style {
          height: 100%;
        }
        .funnel-container {
          display: grid;
          height: 100%;
          width: 100%;
          grid-template-rows: max-content 1fr;
          grid-template-columns: max-content 1fr;
          grid-template-areas:
            '.      audiences'
            'stages funnel';
        }
        .stage-column {
          grid-area: stages;
          height: 100%;
          width: 100%;
          padding-right: 1.14rem;
          display: flex;
          flex-flow: column nowrap;
          transform: scaleY(1.45) perspective(100rem) rotateX(-35deg);
          transform-origin: top;
        }
        .stage-column > div:not(:last-child) {
          border-bottom: 0.14rem solid var(--color-border);
        }
        .stage-name {
          flex: 1 1 100%;
          display: flex;
          justify-content: flex-start;
          align-items: center;
          min-height: 2.28rem;
          color: var(--core-color-core-dark);
          font-weight: 600;
          line-height: 1.42857rem;
          letter-spacing: 0.00307rem;
        }
        .audience-row {
          grid-area: audiences;
          height: 100%;
          width: 100%;
          display: flex;
          flex-flow: row nowrap;
          padding-bottom: 1.14rem;
        }
        .audience-name {
          flex: 1 1 100%;
          display: flex;
          flex-flow: column nowrap;
          justify-content: flex-end;
          align-items: center;
        }
        .audience-name p {
          text-align: center;
          font-weight: 600;
          line-height: 1.42857rem;
          letter-spacing: 0.00307rem;
        }
        .audience-population {
          display: flex;
          flex-flow: row nowrap;
          justify-content: center;
          align-items: center;
        }
        .audience-population p {
          font-size: 0.78571rem;
          font-weight: 400;
          line-height: 1.07143rem;
          letter-spacing: 0.00186rem;
        }
        .funnel {
          grid-area: funnel;
          height: 100%;
          width: 100%;
          display: flex;
          flex-flow: row nowrap;
          transform: scaleY(1.45) perspective(100rem) rotateX(-35deg);
          transform-origin: top;
        }
        .funnel-column {
          display: flex;
          flex-flow: column nowrap;
          flex: 1 1 100%;
        }
        .segment {
          position: relative;
          flex: 1 1 100%;
          display: flex;
          flex-flow: column nowrap;
          justify-content: center;
          align-items: center;
          color: var(--color-core-dark);
          background-color: transparent;
          border-bottom: 0.14rem solid var(--color-core-light);
          border-right: 0.14rem solid var(--color-core-light);
          transition: background-color 0.3s ease-in-out;
          transition: box-shadow 0.3s ease-in-out;
        }

        .pipeline-segment {
          position: absolute;
          bottom: 0;
          justify-content: flex-start;
          min-height: 2.5rem;
          border-top: thin solid var(--color-core-light);
          border-left: thin solid var(--color-core-light);
          background-color: var(--color-core-medium);
        }

        .button-container {
          display: none;
          margin: 0.5rem;
        }
        omni-checkbox {
          margin-right: 0.736rem;
        }
        p.percent-value {
          margin: 0.5rem 0.5rem;
        }
        .segment-selected {
          min-height: 3.5rem !important;
          background-color: var(--color-core-medium-two);
        }

        .segment:hover {
          min-height: 3.5rem !important;
          background-color: var(--color-core-medium-two);
          box-shadow:
            rgba(0, 0, 0, 0.3) 0rem 1rem 2rem,
            rgba(0, 0, 0, 0.22) 0rem 1rem 1rem;
        }
        .grouped-segment {
          background-color: var(--color-core-medium-two);
        }
        .group-onHover {
          box-shadow:
            rgba(0, 0, 0, 0.3) 0rem 1rem 2rem,
            rgba(0, 0, 0, 0.22) 0rem 1rem 1rem;
        }
        .button-container-selected {
          display: flex;
          flex-flow: row nowrap;
          align-items: center;
          gap: 0.5rem;
        }
        .segment:hover .button-container {
          display: flex;
          flex-flow: row nowrap;
          align-items: center;
          gap: 0.5rem;
        }
        .segment button.button.is-text:hover {
          background-color: rgba(39, 35, 65, 0.25);
        }
        .segment-p-selected {
          display: none;
        }
        .segment:hover p {
          display: none;
        }
        @media (min-width: 1500px) {
          .funnel {
            padding: 0 14rem;
          }
          .audience-row {
            padding: 0 14rem;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      audiences: { type: Array },
      stages: { type: Array },
      groups: { type: Object },
      funnelType: { type: String, attribute: 'funnel-type', reflect: true },
      segmentInfoIsHidden: { type: Boolean },
      addSegmentIsHidden: { type: Boolean },
      rfcIsHidden: { type: Boolean },
      audienceView: { type: String, attribute: 'audience-view', reflect: true },
      groupingSegments: { type: Array },
      groupOnHover: { type: String },
    };
  }

  constructor() {
    super();
    this.audiences = [];
    this.stages = [];
    this.groups = {};
    this.selectedAEObject = [];
    this.funnelType = '';
    this.audienceView = '';
    this.segmentInfoIsHidden = true;
    this.addSegmentIsHidden = true;
    this.rfcIsHidden = true;
    this.audienceIndex = undefined;
    this.percentageIndex = undefined;
    this.groupOnHover = null;
    this.columnOpacity = [0.9, 0.7, 0.5, 0.3, 0.1];
  }

  #openSegmentInfo(audienceIndex, percentageIndex) {
    this.audienceIndex = audienceIndex;
    this.percentageIndex = percentageIndex;
    this.segmentInfoIsHidden = false;
  }

  #closeSegmentInfo() {
    this.segmentInfoIsHidden = true;
    this.audienceIndex = 0;
    this.percentageIndex = 0;
  }

  #openAddSegment = (audienceIndex, percentageIndex) => {
    this.audienceIndex = audienceIndex;
    this.percentageIndex = percentageIndex;
    this.addSegmentIsHidden = false;
  };

  #closeAddSegment() {
    this.addSegmentIsHidden = true;
    this.audienceIndex = 0;
    this.percentageIndex = 0;
  }

  #openRFC = (audienceIndex, percentageIndex) => {
    this.audienceIndex = audienceIndex;
    this.percentageIndex = percentageIndex;
    this.rfcIsHidden = false;
  };

  #closeRFC() {
    this.rfcIsHidden = true;
    this.audienceIndex = 0;
    this.percentageIndex = 0;
  }

  #getRoleColor = (isComms, groupRoleColor, segmentRoleColor, percent) => {
    if (isComms) {
      return groupRoleColor ?? segmentRoleColor;
    }
    if (!isComms && percent.group) {
      return 'var(--color-core-medium-two)';
    }
    return 'transparent';
  };

  get #addSegmentTemplate() {
    if (this.addSegmentIsHidden) return nothing;
    return html`
      <campaign-audience-add-segments
        @close-add-segments=${this.#closeAddSegment}
        .context=${this.context}
        .token=${this.token}
        .env=${this.env}
        .audience=${this.audiences[this.audienceIndex]}
        .stage=${this.stages[this.percentageIndex]}
        .groups=${this.groups}
        .selectedAEObject=${this.selectedAEObject}
        .percentageIndex=${this.percentageIndex}
        .audienceIndex=${this.audienceIndex}
      ></campaign-audience-add-segments>
    `;
  }

  get #segmentInfoTemplate() {
    if (this.segmentInfoIsHidden) return nothing;
    return html`
      <campaign-audience-segment-info
        .audience=${this.audiences[this.audienceIndex]}
        .stage=${this.stages[this.percentageIndex]}
        .groups=${this.groups}
        .percentageIndex=${this.percentageIndex}
        .audienceView=${this.audienceView}
        @close-segment-info=${this.#closeSegmentInfo}
      ></campaign-audience-segment-info>
    `;
  }

  get #rfcSlidersTemplate() {
    if (this.rfcIsHidden) return nothing;
    const audience = this.audiences[this.audienceIndex];
    const stage = this.stages[this.percentageIndex];
    const groupName = audience.stagePercentages[this.percentageIndex].group;
    const source = groupName
      ? this.groups[groupName].roleForComms
      : audience.stagePercentages[this.percentageIndex].roleForComms;
    const { description, info, interest, need, role, title } = source;
    return html`
      <campaign-audience-rfc-sliders
        .audience=${audience}
        .stage=${stage}
        .percentageIndex=${this.percentageIndex}
        .audienceIndex=${this.audienceIndex}
        .rfcDescription=${description}
        .info=${info}
        .interest=${interest}
        .need=${need}
        .rfc=${role}
        .rfcTitle=${title}
        @close-rfc-sliders=${this.#closeRFC}
      ></campaign-audience-rfc-sliders>
    `;
  }

  render() {
    const cellBtnClickHandler =
      this.audienceView === 'viz' ? this.#openAddSegment : this.#openRFC;

    return html`
      <omni-style>
        <div class="funnel-container">
          ${this.#addSegmentTemplate} ${this.#segmentInfoTemplate}
          ${this.#rfcSlidersTemplate}
          <div class="stage-column">
            ${repeat(
              this.stages,
              (stage, index) => `${stage.name}-${index}`,
              (stage, stageIndex) => html`
                <div
                  class="stage-name"
                  style=${styleMap({
                    flex: `1 1 ${this.audiences[0].stagePercentages[stageIndex].value}%`,
                  })}
                >
                  ${stage.name}
                </div>
              `,
            )}
          </div>
          <div class="audience-row">
            ${repeat(
              this.audiences,
              audience => audience.id,
              audience => html`
                <div class="audience-name">
                  <p class="is-clamped clamp-2">${audience.name}</p>
                  <div class="audience-population">
                    <p>
                      ${(
                        (audience.idCount / audience.populationCount) *
                        100
                      ).toFixed(2)}
                    </p>
                    %,
                    <p>${formatAudienceNum(audience.idCount)}</p>
                  </div>
                </div>
              `,
            )}
          </div>
          <div class="funnel">
            ${repeat(
              this.audiences,
              audience => audience.id,
              (audience, audienceIndex) => html`
                <div
                  class="funnel-column"
                  style=${styleMap({
                    backgroundColor:
                      this.audienceView === 'comms'
                        ? 'var(--color-core-medium)'
                        : `rgba(var(--rgb-primary), ${this.columnOpacity[audienceIndex]})`,
                    position:
                      this.funnelType === 'pipeline' ? 'relative' : 'static',
                  })}
                >
                  ${repeat(
                    audience.stagePercentages,
                    (percent, index) => `${percent.value}-${index}`,
                    (percent, percentageIndex) => {
                      const checkBoxDisabled = this.groupingSegments?.some(
                        segment => segment.audienceIndex !== audienceIndex,
                      );

                      const segmentSelected = this.groupingSegments?.find(
                        segment =>
                          segment.percentageIndex === percentageIndex &&
                          segment.audienceIndex === audienceIndex,
                      );
                      const groupOnHover = this.groupOnHover === percent.group;

                      const segmentRoleColor =
                        rfcColors[percent.roleForComms?.role];

                      const groupRoleColor =
                        rfcColors[
                          this.groups[percent.group]?.roleForComms?.role
                        ];

                      const isComms = this.audienceView === 'comms';

                      const segmentColor = this.#getRoleColor(
                        isComms,
                        groupRoleColor,
                        segmentRoleColor,
                        percent,
                      );

                      return html`
                        <div
                          @mouseenter=${() => {
                            if (!percent.group?.length) return;
                            this.groupOnHover = percent.group;
                          }}
                          @mouseleave=${() => {
                            this.groupOnHover = null;
                          }}
                          class="${classMap({
                            segment: true,
                            'pipeline-segment': this.funnelType === 'pipeline',
                            'segment-selected': segmentSelected,
                            'grouped-segment': percent.group,
                            'group-onHover': groupOnHover,
                          })}"
                          style=${styleMap({
                            flex:
                              this.funnelType === 'pipeline'
                                ? 'none'
                                : `1 1 ${percent.value}%`,
                            backgroundColor: segmentColor,
                            zIndex:
                              this.funnelType === 'pipeline'
                                ? percentageIndex
                                : 'auto',
                            height:
                              this.funnelType === 'pipeline'
                                ? `${percent.value}%`
                                : 'auto',
                            width:
                              this.funnelType === 'pipeline'
                                ? `calc(100% - ${percentageIndex * 1}rem)`
                                : 'auto',
                            marginLeft:
                              this.funnelType === 'pipeline'
                                ? `${percentageIndex * 1}rem`
                                : 0,
                          })}
                        >
                          <div
                            class="${classMap({
                              'button-container': true,
                              'button-container-selected': segmentSelected,
                            })}"
                          >
                            ${percent.group && this.audienceView === 'viz'
                              ? html`
                                  <omni-tooltip>
                                    <div slot="content">Ungroup Segments</div>
                                    <button
                                      class="button is-text"
                                      @click=${() => {
                                        this.dispatchNewEvent(
                                          'ungroup-segments',
                                          {
                                            detail: percent.group,
                                          },
                                        );
                                      }}
                                    >
                                      <omni-icon
                                        class="is-size-1"
                                        icon-id="omni:interactive:unlink"
                                      ></omni-icon>
                                    </button>
                                  </omni-tooltip>
                                `
                              : html`
                                  <omni-tooltip>
                                    <div slot="content">
                                      ${checkBoxDisabled
                                        ? 'Only segments within the same audience can be grouped'
                                        : 'Group Segments'}
                                    </div>
                                    <omni-checkbox
                                      ?disabled=${checkBoxDisabled}
                                      ?hidden=${this.audienceView === 'comms'}
                                      @change=${e => {
                                        this.dispatchNewEvent(
                                          'group-segments',
                                          {
                                            detail: {
                                              audienceIndex,
                                              percentageIndex,
                                              checked: e.target.checked,
                                            },
                                          },
                                        );
                                      }}
                                    ></omni-checkbox>
                                  </omni-tooltip>
                                `}
                            <omni-tooltip>
                              <div slot="content">
                                ${this.audienceView === 'viz'
                                  ? 'Manage Custom Segments'
                                  : 'Manage Role for Comms'}
                              </div>
                              <button
                                class="button is-text"
                                @click=${() =>
                                  cellBtnClickHandler(
                                    audienceIndex,
                                    percentageIndex,
                                  )}
                              >
                                <omni-icon
                                  class="is-size-1"
                                  icon-id=${this.audienceView === 'viz'
                                    ? 'omni:object:audience'
                                    : 'omni:app:performanceReporting'}
                                ></omni-icon>
                              </button>
                            </omni-tooltip>
                            <omni-tooltip>
                              <div slot="content">View Info</div>
                              <button
                                ?disabled=${!(
                                  percent.customSegments?.length > 0 ||
                                  this.groups[percent.group]?.customSegments
                                    ?.length > 0 ||
                                  percent.roleForComms?.role ||
                                  this.groups[percent.group]?.roleForComms?.role
                                )}
                                class="button is-text"
                                @click=${() =>
                                  this.#openSegmentInfo(
                                    audienceIndex,
                                    percentageIndex,
                                  )}
                              >
                                <omni-icon
                                  class="is-size-1"
                                  icon-id="omni:informative:info"
                                ></omni-icon>
                              </button>
                            </omni-tooltip>
                          </div>
                          <p
                            class="${classMap({
                              'percent-value': true,
                              'segment-p-selected': segmentSelected,
                            })}"
                          >
                            ${percent.value}%
                          </p>
                        </div>
                      `;
                    },
                  )}
                </div>
              `,
            )}
          </div>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-audience-funnel-viz', CampaignAudienceFunnelViz);
